// Het tijdstip van een contactmoment: invullen in het formulier en tonen in de
// lijst. Nodig omdat de lijst alleen een datum toonde en het tijdstip niet te
// kiezen was — terwijl een coach juist wil zien hoe laat er al gebeld is, om
// een volgende poging op een ander moment van de dag te doen.
//
// Het tijdstip staat in `Timestamp`, het veld waarop de lijst, de reeks
// mislukte pogingen en 'LAATSTE CONTACT' al sorteren. Een eigen veld zou die
// drie laten rekenen met het moment van invoeren in plaats van het moment van
// contact.
// AI-CONTEXT: See docs/features/contact-moments.md

const pad = (n) => String(n).padStart(2, '0')

/**
 * Waarde voor een `<input type="datetime-local">`, in lokale tijd.
 *
 * @param {Date} date
 * @returns {string} Bijvoorbeeld '2026-10-01T09:05'
 */
const toDateTimeInputValue = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`

/**
 * Leest de waarde van een `<input type="datetime-local">` als lokale tijd.
 *
 * Met de hand ontleed en niet via `new Date(value)`: die accepteert ook een
 * kale datum (en leest die dan als UTC) en rolt 30 februari stil door naar
 * maart.
 *
 * @param {string} [value]
 * @returns {Date|null} null als de waarde geen bestaande datum met tijd is
 */
const parseDateTimeInputValue = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || '')
    if (!match) return null
    const [year, month, day, hours, minutes] = match.slice(1).map(Number)
    const date = new Date(year, month - 1, day, hours, minutes)
    if (date.getMonth() !== month - 1 || date.getDate() !== day) return null
    return date
}

/**
 * Een contactmoment is iets dat al gebeurd is; een volgende poging plannen
 * doe je met een afspraak.
 *
 * @param {Date|null} date - Gekozen tijdstip
 * @param {Date} now
 * @returns {boolean}
 */
const isAllowedContactMomentTime = (date, now) =>
    date instanceof Date && date.getTime() <= now.getTime()

/**
 * Het tijdstip dat in `Timestamp` komt: de gekozen datum en minuut, aangevuld
 * met de seconden en milliseconden van het moment van invoeren.
 *
 * Het veld kiest per minuut. Zonder aanvulling krijgen twee pogingen in
 * dezelfde minuut (geen gehoor, meteen terugbellen, bereikt) exact hetzelfde
 * tijdstip, en dan is hun volgorde in de lijst én in de reeks mislukte
 * pogingen willekeurig. De lijst toont alleen minuten, dus de aanvulling is
 * niet te zien. Nooit in de toekomst: binnen de huidige minuut zijn de
 * seconden van nu, daarbuiten ligt de gekozen minuut al in het verleden.
 * AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
 *
 * @param {Date} chosen - Gekozen tijdstip, op de minuut
 * @param {Date} now - Moment van invoeren
 * @returns {Date}
 */
const toContactMomentDate = (chosen, now) => {
    const date = new Date(chosen.getTime())
    date.setSeconds(now.getSeconds(), now.getMilliseconds())
    return date
}

/**
 * Datum en tijd voor in de lijst.
 *
 * Een geïmporteerd contactmoment krijgt alleen de datum: de import kende geen
 * tijd en zette er 09:00 of het importmoment op, en dat is niet het moment
 * waarop er gebeld is (functions/migrations/importTneHouseholds.js).
 *
 * @param {{Timestamp?: {toDate: () => Date}|null, ImportBatch?: string}} moment
 * @returns {string}
 */
const formatContactMomentWhen = (moment) => {
    if (typeof moment?.Timestamp?.toDate !== 'function') return '—'
    const date = moment.Timestamp.toDate()
    const day = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
    if (moment.ImportBatch) return day
    return `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export {
    toDateTimeInputValue,
    parseDateTimeInputValue,
    isAllowedContactMomentTime,
    toContactMomentDate,
    formatContactMomentWhen,
}
